import { useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Select,
  Typography
} from '@mui/material';

import { UserRole } from '../../enums';
import { camelCaseToTitle } from '../../utils/caseConversion';
import Icons from '../../components/icons';
import {
  RoleAccordion,
  RoleAccordionDetails,
  RoleAccordionSummary,
  Subtitle1Large
} from './styled';
import { Control, UseFormSetValue } from 'react-hook-form';
import { initialSchoolConfigProps } from './types';
import {
  newRoleMapping,
  rolesExcludedForParentsPostPublish
} from './config/constants';
import CustomMenuItem from '../../components/CustomMenuItem/CustomMenuItem';
import StyledCheckbox from '../common/styled/Checkbox';

interface RoleFeatureMappingProps {
  control: Control<any>;
  setValue: UseFormSetValue<initialSchoolConfigProps>;
}

const getFeatureLabel = (featureKey: string) => {
  switch (featureKey) {
    case 'publishDCU':
      return 'Allow Publish DCU';
    case 'addRemarksDcu':
      return 'Allow to Add Remarks';
    default:
      return camelCaseToTitle(featureKey);
  }
};

const RoleFeatureMapping = ({ control, setValue }: RoleFeatureMappingProps) => {
  const watch = control._getWatch;
  const formData: initialSchoolConfigProps = watch();

  const roleMappings: any[] = useMemo(() => {
    const current = (formData as any)?.roleFeaturesMapping;
    return Array.isArray(current) ? current : [];
  }, [formData]);

  const addNewRoleFeatureMapping = () => {
    const current = Array.isArray(formData.roleFeaturesMapping)
      ? formData.roleFeaturesMapping
      : [];

    setValue('roleFeaturesMapping', [...current, { ...newRoleMapping }]);
  };

  const handleRemoveRoleFeatureMapping = (index: number) => {
    const updated = [...roleMappings];
    updated.splice(index, 1);
    setValue('roleFeaturesMapping', updated);
  };

  const handleRoleChange = (index: number, value: string) => {
    const updated = [...roleMappings];
    updated[index] = { ...updated[index], role: value };

    setValue('roleFeaturesMapping', updated);
  };

  const handleFeatureToggle = (
    index: number,
    featureKey: string,
    enabled: boolean
  ) => {
    const updated = [...roleMappings];
    updated[index].features[featureKey] = {
      ...updated[index].features[featureKey],
      enabled
    };
    setValue('roleFeaturesMapping', updated);
  };

  const handleMetaToggle = (
    index: number,
    featureKey: string,
    metaKey: 'subjectTeacher' | 'classTeacher',
    enabled: boolean
  ) => {
    const updated = [...roleMappings];
    const prevMeta = updated[index].features[featureKey]?.metaData || {};

    updated[index].features[featureKey] = {
      ...updated[index].features[featureKey],
      metaData: {
        ...prevMeta,
        [metaKey]: { enabled }
      }
    };

    setValue('roleFeaturesMapping', updated);
  };

  return (
    <Box>
      {formData.roleFeaturesMapping?.map((mapping: any, index: number) => {
        return (
          <RoleAccordion key={`role-mapping-${mapping.role || index}`}>
            <RoleAccordionSummary
              expandIcon={<Icons.CaretDown />}
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
            >
              <Grid
                container
                sx={{ alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Grid container>
                  {mapping.role ? (
                    <Typography variant='h6'>
                      {camelCaseToTitle(mapping.role)}
                    </Typography>
                  ) : (
                    <FormControl
                      variant='outlined'
                      style={{ minWidth: '250px' }}
                    >
                      <InputLabel id='role-select-label'>
                        Select Role
                      </InputLabel>
                      <Select
                        labelId={`role-select-label-${index}`}
                        id={`role-select-${index}`}
                        value={mapping.role || ''}
                        onChange={(e) =>
                          handleRoleChange(index, e.target.value as string)
                        }
                        label='Select Role'
                      >
                        {Object.entries(UserRole)
                          .filter(([_, value]) => {
                            const isExcluded =
                              rolesExcludedForParentsPostPublish.includes(
                                value as UserRole
                              );
                            const isAlreadyUsed = roleMappings.some(
                              (m: any) => m.role === value
                            );
                            return !isExcluded && !isAlreadyUsed;
                          })
                          .map(([_, value]) => (
                            <CustomMenuItem key={value} value={value}>
                              {camelCaseToTitle(value)}
                            </CustomMenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  )}
                </Grid>
              </Grid>
            </RoleAccordionSummary>
            <RoleAccordionDetails>
              {mapping.role === (UserRole.TEACHER as string) ? (
                <Box>
                  <Grid container spacing={1} alignItems='center'>
                    <Grid size={{ xs: 12 }}>
                      <Subtitle1Large>
                        {getFeatureLabel('publishDCU')}
                      </Subtitle1Large>
                      <Grid
                        container
                        spacing={1}
                        style={{ marginLeft: '32px' }}
                      >
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControlLabel
                            control={
                              <StyledCheckbox
                                checked={
                                  mapping.features?.publishDcu?.metaData
                                    ?.subjectTeacher?.enabled ?? false
                                }
                                onChange={(e) =>
                                  handleMetaToggle(
                                    index,
                                    'publishDcu',
                                    'subjectTeacher',
                                    e.target.checked
                                  )
                                }
                                color='primary'
                              />
                            }
                            label='Subject Teacher'
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControlLabel
                            control={
                              <StyledCheckbox
                                checked={
                                  mapping.features?.publishDcu?.metaData
                                    ?.classTeacher?.enabled ?? false
                                }
                                onChange={(e) =>
                                  handleMetaToggle(
                                    index,
                                    'publishDcu',
                                    'classTeacher',
                                    e.target.checked
                                  )
                                }
                                color='primary'
                              />
                            }
                            label='Class Teacher'
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Subtitle1Large>
                        {getFeatureLabel('addRemarksDcu')}
                      </Subtitle1Large>
                      <Grid
                        container
                        spacing={1}
                        style={{ marginLeft: '32px' }}
                      >
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControlLabel
                            control={
                              <StyledCheckbox
                                checked={
                                  mapping.features?.addRemarksDcu?.metaData
                                    ?.subjectTeacher?.enabled ?? false
                                }
                                onChange={(e) =>
                                  handleMetaToggle(
                                    index,
                                    'addRemarksDcu',
                                    'subjectTeacher',
                                    e.target.checked
                                  )
                                }
                                color='primary'
                              />
                            }
                            label='Subject Teacher'
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControlLabel
                            control={
                              <StyledCheckbox
                                checked={
                                  mapping.features?.addRemarksDcu?.metaData
                                    ?.classTeacher?.enabled ?? false
                                }
                                onChange={(e) =>
                                  handleMetaToggle(
                                    index,
                                    'addRemarksDcu',
                                    'classTeacher',
                                    e.target.checked
                                  )
                                }
                                color='primary'
                              />
                            }
                            label='Class Teacher'
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Grid container spacing={2} alignItems='center'>
                  {Object.entries(mapping.features).map(
                    ([featureKey, featureValue]: any) => (
                      <Grid
                        key={`feature-${featureKey}-${index}`}
                        size={{ xs: 12, sm: 4 }}
                      >
                        <FormControlLabel
                          control={
                            <StyledCheckbox
                              checked={!!featureValue.enabled}
                              onChange={(e) =>
                                handleFeatureToggle(
                                  index,
                                  featureKey,
                                  e.target.checked
                                )
                              }
                              color='primary'
                            />
                          }
                          label={getFeatureLabel(featureKey)}
                        />
                      </Grid>
                    )
                  )}
                </Grid>
              )}
              <Box position='absolute' bottom={16} right={24} zIndex={1}>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveRoleFeatureMapping(index);
                  }}
                  style={{ minWidth: 0, padding: 8 }}
                >
                  <Icons.Delete />
                </Button>
              </Box>
            </RoleAccordionDetails>
          </RoleAccordion>
        );
      })}

      <Box marginTop={2}>
        <Button
          variant='contained'
          color='primary'
          startIcon={<Icons.PlusWhite />}
          onClick={addNewRoleFeatureMapping}
        >
          Add New Role
        </Button>
      </Box>
    </Box>
  );
};

export default RoleFeatureMapping;
